/**
 * Media gallery component for displaying decrypted images and videos
 * Features: swipe gestures, clickable thumbnails, fullscreen mode
 */

import { useState } from 'react';

interface MediaItem {
  id: string;
  kind: 'image' | 'video';
  mime: string;
  dataUrl: string;
  width: number | null;
  height: number | null;
  /** Optional per-photo detail, decrypted. Null when the photo has none. */
  detail: string | null;
}

interface MediaGalleryProps {
  items: MediaItem[];
  /** Notified whenever the visible photo changes, so the page can show its detail. */
  onIndexChange?: (index: number) => void;
}

export default function MediaGallery({ items, onIndexChange }: MediaGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);

  if (items.length === 0) {
    return null;
  }

  const currentItem = items[currentIndex];

  // Single funnel for every index change, so the caller is always told which
  // photo is visible — the per-photo detail is rendered outside this component.
  const selectIndex = (next: number) => {
    setCurrentIndex(next);
    onIndexChange?.(next);
  };

  const goToPrevious = () => {
    selectIndex(currentIndex > 0 ? currentIndex - 1 : items.length - 1);
  };

  const goToNext = () => {
    selectIndex(currentIndex < items.length - 1 ? currentIndex + 1 : 0);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goToNext();
      else goToPrevious();
    }
    setTouchStart(null);
  };

  return (
    <>
      {/* Main Gallery */}
      <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-white/5 to-white/0 border border-white/10">
        {/* Current Media - swipeable */}
        <div
          className="relative select-none cursor-grab active:cursor-grabbing bg-black/20"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onClick={() => setIsFullscreen(true)}
        >
          {currentItem.kind === 'image' ? (
            <img
              src={currentItem.dataUrl}
              alt=""
              className="w-full h-auto max-h-[70vh] object-contain mx-auto"
              draggable={false}
            />
          ) : (
            <video
              src={currentItem.dataUrl}
              controls
              className="w-full h-auto max-h-[70vh] object-contain mx-auto"
              onClick={(e) => e.stopPropagation()}
            />
          )}

          {/* Navigation Arrows */}
          {items.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors backdrop-blur-sm"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors backdrop-blur-sm"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Image counter */}
          {items.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-sm">
              {currentIndex + 1} / {items.length}
            </div>
          )}
        </div>

        {/* Thumbnail strip - clickable */}
        {items.length > 1 && (
          <div className="flex gap-1 p-2 bg-black/30 overflow-x-auto">
            {items.map((item, index) => (
              <button
                key={item.id}
                onClick={() => selectIndex(index)}
                className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all ${
                  index === currentIndex
                    ? 'ring-2 ring-waffle-orange opacity-100'
                    : 'opacity-60 hover:opacity-100'
                }`}
              >
                {item.kind === 'image' ? (
                  <img
                    src={item.dataUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                    <svg className="w-6 h-6 text-white/70" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div
          className="fixed inset-0 z-50 bg-black flex items-center justify-center"
          onClick={() => setIsFullscreen(false)}
        >
          <button
            onClick={() => setIsFullscreen(false)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors z-10"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {currentItem.kind === 'image' ? (
            <img
              src={currentItem.dataUrl}
              alt=""
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <video
              src={currentItem.dataUrl}
              controls
              autoPlay
              className="max-w-full max-h-full"
              onClick={(e) => e.stopPropagation()}
            />
          )}

          {/* Fullscreen Navigation */}
          {items.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}
